import type {
  ComponentPropsWithoutRef,
  ComponentType,
  FC,
  ReactNode,
} from 'react';
import { ActionModeProvider } from 'src/components/action-mode';
import type { IconProps } from 'src/components/media-and-icons/icon';
import { Heading } from 'src/components/text/heading';
import { cn } from 'src/utils/cn';
import type { StrictOmit } from 'src/utils/utility-types';

export type EmptyDatasetProps = {
  icon?: ComponentType<IconProps>;
  title?: string;
  description: string;
  /** 2 actions at most, wrapped in a Fragment. If you want to insert more actions/buttons, consider using <a href="./?path=/docs/components-buttons-actions-split-actions--docs">Split Actions</a> component to group them. */
  actions?: ReactNode;
  'data-testid'?: string;
} & StrictOmit<ComponentPropsWithoutRef<'div'>, 'title'>;

export const EmptyDataset: FC<EmptyDatasetProps> = ({
  icon: IconComponent,
  title,
  description,
  actions,
  className,
  ['data-testid']: testId = 'empty-dataset',
  ...props
}) => {
  return (
    <div
      className={cn(
        'grid justify-items-center gap-xl p-2xl text-center',
        className
      )}
      data-testid={testId}
      {...props}
    >
      {IconComponent && (
        <div
          className="text-foreground-surface-subdued"
          data-testid={`${testId}-icon`}
        >
          <IconComponent size="md" />
        </div>
      )}

      <div
        className="grid max-w-[40rem] justify-items-center gap-md"
        data-testid={`${testId}-content`}
      >
        {title && (
          <Heading mode="title" size="sm" data-testid={`${testId}-title`}>
            {title}
          </Heading>
        )}
        <p
          className="text-foreground-surface-subdued"
          data-testid={`${testId}-description`}
        >
          {description}
        </p>
      </div>

      {actions && (
        <div
          className="flex flex-wrap items-center justify-center gap-lg"
          data-testid={`${testId}-actions`}
        >
          <ActionModeProvider value={{ mode: 'button-secondary', size: 'sm' }}>
            {actions}
          </ActionModeProvider>
        </div>
      )}
    </div>
  );
};
